import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { aiScriptSpreadsheetSheetListKey } from "@/payroll/ai-script/spreadsheet-sheet-query";
import type { IAcceptAiScriptCellOverrideResponse } from "@/payroll/ai-script/types";
import { isAiScriptWorkbookAuthorizationError } from "@/payroll/ai-script/utils";
import { workbookMutationRequestConfig } from "@/payroll/ai-script/workbook-mutation";
import {
  aiScriptCellOverrideHistoryPrefixKey,
  aiScriptCellOverridesKey,
  aiScriptSpreadsheetKey,
} from "./keys";
import { getAiScriptCellOverridesPath } from "./paths";
import { executeWorkbookMutation } from "./workbook-refresh";

const refreshOverrideAccess = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: aiScriptCellOverridesKey(businessId, runId),
  });
  queryClient.invalidateQueries({
    queryKey: aiScriptSpreadsheetKey(businessId, runId),
  });
};

export const useAcceptAiScriptCellOverride = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
    }: {
      runId: string;
      overrideId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post(
          `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/accept`,
        )
        .then((res) => res.data as IAcceptAiScriptCellOverrideResponse);
    },
    onSuccess: (_response, { runId }) => {
      queryClient.invalidateQueries({
        queryKey: aiScriptCellOverridesKey(businessId, runId),
      });
      queryClient.invalidateQueries({
        queryKey: aiScriptCellOverrideHistoryPrefixKey(businessId, runId),
      });
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetSheetListKey(businessId, runId),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

export const useDismissAiScriptCellOverride = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
    }: {
      runId: string;
      overrideId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .delete(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}`,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

export const useDisambiguateAiScriptCellOverride = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
      cellId,
    }: {
      runId: string;
      overrideId: string;
      cellId: string;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = { cell_id: cellId };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/disambiguate`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

export const useRestoreOverrideVersion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      overrideId,
      targetVersionId,
      baseVersion,
    }: {
      runId: string;
      overrideId: string;
      targetVersionId: string;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        target_version_id: targetVersionId,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};

export const useDiscardAllEdits = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      baseVersion,
    }: {
      runId: string;
      baseVersion?: number;
    }) => {
      if (!businessId) throw new Error("Business is required");

      const body = baseVersion != null ? { base_version: baseVersion } : {};
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptCellOverridesPath(businessId, runId)}/discard_all`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onError: (error, { runId }) => {
      if (!isAiScriptWorkbookAuthorizationError(error)) return;
      refreshOverrideAccess(queryClient, businessId, runId);
    },
  });
};
